import React, { useState, useRef } from 'react';
import { Box, Image, Text, IconButton, Flex, useToast, Spinner } from '@chakra-ui/react';
import { FiCamera, FiTrash2 } from 'react-icons/fi';
import api from '../api';

export default function ImageUpload({ folder, entityType, entityId, currentFileId, onUpdate, size = '60px', label = 'Photo' }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const toast = useToast();

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Only images allowed', status: 'warning', duration: 2000 });
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', folder);
      formData.append('entity_type', entityType);
      formData.append('entity_id', String(entityId));
      const { data } = await api.post('/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Delete old file if exists
      if (currentFileId) {
        try { await api.delete(`/files/${currentFileId}`); } catch (e) {}
      }
      onUpdate(data.id, data);
      toast({ title: `${label} uploaded`, status: 'success', duration: 1500 });
    } catch (err) {
      toast({ title: err.response?.data?.error || 'Upload failed', status: 'error', duration: 2000 });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!currentFileId) return;
    try {
      await api.delete(`/files/${currentFileId}`);
      onUpdate(null, null);
      toast({ title: `${label} removed`, status: 'info', duration: 1500 });
    } catch (e) {}
  };

  // Try to get the image URL from files
  const [imgUrl, setImgUrl] = useState(null);
  React.useEffect(() => {
    if (currentFileId) {
      api.get(`/files?entity_type=${entityType}&entity_id=${entityId}`).then(({ data }) => {
        const f = data.find(f => f.id === currentFileId);
        if (f) setImgUrl(`/api/files/${f.folder}/${f.stored_name}`);
        else setImgUrl(null);
      }).catch(() => setImgUrl(null));
    } else { setImgUrl(null); }
  }, [currentFileId, entityType, entityId]);

  return (
    <Box>
      <input type="file" ref={fileRef} style={{ display: 'none' }} accept="image/*" onChange={handleUpload} />
      <Flex direction="column" align="center" gap={1}>
        <Box w={size} h={size} borderRadius={folder === 'avatars' ? 'full' : 'md'}
          border="2px dashed" borderColor={imgUrl ? 'transparent' : 'gray.200'}
          overflow="hidden" cursor="pointer" onClick={() => fileRef.current?.click()}
          display="flex" alignItems="center" justifyContent="center" bg="gray.50">
          {uploading ? (
            <Spinner size="sm" />
          ) : imgUrl ? (
            <Image src={imgUrl} w="100%" h="100%" objectFit="cover" />
          ) : (
            <FiCamera size={16} color="gray" />
          )}
        </Box>
        {imgUrl && (
          <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red"
            onClick={handleDelete} aria-label={`Remove ${label}`} />
        )}
      </Flex>
    </Box>
  );
}
